import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Input, Table } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { beeXLogger } from '../../../../helpers/logger';
import './index.scss';
import { MonitoringPaper } from '../MonitoringPaper';
import { useTranslation } from 'react-i18next';
import { useMonitoringDataWithInterval } from '../../../../hooks/useMonitoringDataWithInterval';
import { useMonitoringDataFetch } from '../../../../hooks/useMonitoringDataFetch';
import { formatDurationSmart } from '../ImageQuality/ImageHealthLog';

const CONNECTION_LOG_QUESTION_ID = 293;

export const ConnectionLog = ({
  isActive = true,
  intervalMs = 0,
  onRegisterReload,
  onDataLoaded,
  selectedDate,
}) => {
  const [fullData, setFullData] = useState([]);
  const [dataPageSize, setDataPageSize] = useState(10);
  const [current, setCurrent] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const { namespace } = useSelector((state) => state);
  const { t } = useTranslation();

  useEffect(() => {
    if (!isActive) {
      setDataPageSize(10);
      setCurrent(1);
      setSearchValue('');
    }
  }, [isActive]);

  const baseFetchData = useMonitoringDataFetch(
    CONNECTION_LOG_QUESTION_ID,
    selectedDate,
  );

  const fetchConnectionLogData = useCallback(async () => {
    if (!namespace) return null;

    try {
      const rawData = await baseFetchData();
      const transformedData = rawData.map((item) => {
        const durationSeconds = moment(item.endTime).diff(
          moment(item.startTime),
          'seconds',
        );

        return {
          id: item.serial,
          cameraName: item.name,
          serial: item.serial,
          gatewayName: item.nameGateway,
          location: item?.location
            ? item?.location === 'undefined'
              ? '_'
              : item?.location
            : '_',
          startTimeStr: moment(item.startTime).format('YYYY-MM-DD HH:mm:ss'),
          endTimeStr: moment(item.endTime).format('YYYY-MM-DD HH:mm:ss'),
          startTime: item.startTime,
          action: 'no-action',
          duration: durationSeconds,
          durationStr: formatDurationSmart(durationSeconds),
        };
      });

      setFullData(transformedData);
      setCurrent(1);
      if (onDataLoaded && typeof onDataLoaded === 'function') {
        onDataLoaded();
      }
      return transformedData;
    } catch (err) {
      beeXLogger.error(err);
      throw err;
    }
  }, [baseFetchData, namespace, onDataLoaded]);

  const { reload } = useMonitoringDataWithInterval(
    fetchConnectionLogData,
    isActive ? intervalMs : 0,
    [namespace, isActive],
  );

  React.useEffect(() => {
    if (onRegisterReload && typeof onRegisterReload === 'function') {
      onRegisterReload(reload);
    }
  }, [reload, onRegisterReload]);

  const getActionLabel = (action) => {
    const actionMap = {
      'no-action': 'NO ACTION',
    };
    return actionMap[action] || action?.toUpperCase();
  };

  const columns = [
    {
      title: 'CAMERA',
      dataIndex: 'cameraName',
      key: 'cameraName',
      align: 'center',
      render: (text, record) => (
        <div className="camera-cell">
          <div className="camera-name">{text}</div>
          {/* <div className="camera-serial">{record.serial}</div> */}
        </div>
      ),
    },
    {
      title: 'LOCATION',
      dataIndex: 'location',
      key: 'location',
      align: 'center',
      render: (text) => <span>{text}</span>,
    },
    {
      title: 'EVENT',
      dataIndex: 'event',
      key: 'event',
      align: 'center',
      render: () => (
        <span style={{ color: '#f52f39', fontWeight: 'bold' }}>Offline</span>
      ),
    },
    {
      title: 'DETECTED',
      dataIndex: 'startTimeStr',
      key: 'startTimeStr',
      align: 'center',
      render: (text) => <span>{text}</span>,
    },
    {
      title: 'DURATION',
      dataIndex: 'durationStr',
      key: 'durationStr',
      align: 'center',
      render: (text) => <span>{text}</span>,
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (text) => (
        <span style={{ color: '#0e9347', fontWeight: 'bold' }}>Resolve</span>
      ),
    },
    {
      title: 'RESOLVED AT',
      dataIndex: 'endTimeStr',
      key: 'endTimeStr',
      align: 'center',
      render: (text) => <span>{text}</span>,
    },
    // {
    //   title: 'ACTIONS',
    //   dataIndex: 'action',
    //   key: 'action',
    //   width: '18%',
    //   align: 'right',
    //   render: () => (
    //     <button className="action-btn action-no-action">
    //       {getActionLabel('no-action')}
    //     </button>
    //   ),
    // },
  ];

  const filteredData = useMemo(() => {
    if (!searchValue.trim()) {
      return fullData;
    }
    return fullData.filter(
      (item) =>
        item.cameraName.toLowerCase().includes(searchValue.toLowerCase()) ||
        item.serial.toLowerCase().includes(searchValue.toLowerCase()) ||
        item.nameGateway.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [fullData, searchValue]);

  const sortedData = useMemo(() => {
    return [...filteredData].sort(
      (a, b) => moment(b.startTime).valueOf() - moment(a.startTime).valueOf(),
    );
  }, [filteredData]);

  const paginatedData = useMemo(() => {
    const startIndex = (current - 1) * dataPageSize;
    const endIndex = startIndex + dataPageSize;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, current, dataPageSize]);

  const onPageChange = (page, pageSize) => {
    setCurrent(page);
  };

  const onShowSizeChange = (current, pageSize) => {
    setDataPageSize(pageSize);
    setCurrent(1);
  };

  return (
    <MonitoringPaper>
      <div className="connection-log">
        <div className="log-header">
          <h2 className="log-title">{t('Comprehensive Camera Status')}</h2>
          <Input
            placeholder="Search by camera name or serial..."
            prefix={<SearchOutlined />}
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              setCurrent(1);
            }}
            allowClear
            className="search-input"
            width={500}
          />
        </div>

        <Table
          columns={columns}
          dataSource={paginatedData.map((item, index) => ({
            ...item,
            key: item.id + index + 1,
          }))}
          pagination={{
            onChange: onPageChange,
            onShowSizeChange: onShowSizeChange,
            pageSize: dataPageSize,
            current,
            total: sortedData.length,
            position: ['bottomRight'],
            pageSizeOptions: ['10', '20', '50', '100'],
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]} - ${range[1]} of ${total}`,
          }}
          className="connection-table"
          bordered={false}
          size="small"
        />
      </div>
    </MonitoringPaper>
  );
};
